import { router } from '@inertiajs/vue3'
import posthog from 'posthog-js'
import type { App } from 'vue'

export interface PostHogConfig {
    apiKey: string
    apiHost?: string
    enableSessionRecording?: boolean
    enableHeatmaps?: boolean
    capturePageview?: boolean
    capturePageleave?: boolean
}

let posthogInstance: typeof posthog | null = null

export function initPostHog(config: PostHogConfig) {
    if (!config.apiKey) {
        console.warn('PostHog API key not provided, skipping initialization')
        return null
    }

    // Initialize PostHog with configuration
    posthog.init(config.apiKey, {
        api_host: config.apiHost || 'https://app.posthog.com',
        capture_pageview: config.capturePageview ?? false, // We'll handle this manually
        capture_pageleave: config.capturePageleave ?? true,
        autocapture: true, // Automatically capture clicks, form submissions, etc.
        disable_session_recording: !(config.enableSessionRecording ?? true),
        mask_all_text: false, // Don't mask text by default
        mask_all_element_attributes: false,
        enable_heatmaps: config.enableHeatmaps ?? true,
        loaded: posthog => {
            // Identify if user is available
            const windowWithInertia = window as any
            const admin = windowWithInertia.Inertia?.page?.props?.admin

            // Skip identification when staff is impersonating
            if (admin?.is_impersonating) {
                return
            }

            const user = windowWithInertia.Inertia?.page?.props?.user
            const workspace =
                windowWithInertia.Inertia?.page?.props?.workspace ||
                windowWithInertia.Inertia?.page?.props?.current_website

            if (user?.id) {
                // Identify user with properties
                posthog.identify(user.id.toString(), {
                    email: user.email,
                    name: user.name,
                    created_at: user.created_at,
                    is_admin: user.is_admin || false,
                    has_2fa_enabled: user.two_factor_enabled || false,
                })

                // Set user group (workspace/company)
                if (workspace?.id) {
                    posthog.group('company', workspace.id.toString(), {
                        name: workspace.name,
                        domain: workspace.domain || workspace.url,
                        plan: workspace.plan,
                        created_at: workspace.created_at,
                        trial_ends_at: workspace.trial_ends_at,
                        tracker_installed:
                            workspace.last_data_received_at !== null,
                    })
                }
            }
        },
    })

    posthogInstance = posthog

    // Set up Inertia navigation tracking
    setupInertiaTracking()

    return posthog
}

function setupInertiaTracking() {
    if (!posthogInstance) return

    // Track page views on Inertia navigation
    router.on('navigate', event => {
        const page = event.detail.page

        // Skip tracking when staff is impersonating
        const admin = page.props.admin as
            | { is_impersonating?: boolean }
            | undefined
        if (admin?.is_impersonating) {
            return
        }

        const url = window.location.href
        const path = window.location.pathname

        // Capture page view with additional context
        posthogInstance.capture('$pageview', {
            $current_url: url,
            $pathname: path,
            component: page.component,
            props: page.props,
        })

        // Update user identification if user data changes
        const user = page.props.user as any
        const workspace = (page.props.workspace ||
            page.props.current_website) as any

        if (user?.id) {
            posthogInstance.identify(user.id.toString(), {
                email: user.email,
                name: user.name,
                created_at: user.created_at,
                is_admin: user.is_admin || false,
                has_2fa_enabled: user.two_factor_enabled || false,
            })

            // Update workspace group
            if (workspace?.id) {
                posthogInstance.group('company', workspace.id.toString(), {
                    name: workspace.name,
                    domain: workspace.domain || workspace.url,
                    plan: workspace.plan,
                    created_at: workspace.created_at,
                    trial_ends_at: workspace.trial_ends_at,
                    tracker_installed: workspace.last_data_received_at !== null,
                })
            }
        }

        // Track specific page views
        trackSpecificPageViews(path, page.component)
    })

    // Track onboarding completion
    router.on('success', event => {
        const path = window.location.pathname
        if (path === '/onboarding/success') {
            posthogInstance.capture('onboarding_completed')
        }
    })
}

function trackSpecificPageViews(path: string, component: string) {
    // Track onboarding steps
    if (path.includes('/onboarding/')) {
        const step = path.split('/').pop()
        posthogInstance?.capture('onboarding_step_viewed', {
            step: step,
            component: component,
        })
    }

    // Track feature page views
    const featurePages = {
        '/dashboard': 'dashboard',
        '/leads': 'leads',
        '/segments': 'segments',
        '/automations': 'automations',
        '/reports': 'reports',
        '/integrations': 'integrations',
        '/settings': 'settings',
        '/team': 'team_management',
        '/billing': 'billing',
    }

    for (const [pagePath, feature] of Object.entries(featurePages)) {
        if (path.startsWith(pagePath)) {
            posthogInstance?.capture('feature_viewed', {
                feature: feature,
                full_path: path,
            })
            break
        }
    }
}

// Export functions for manual event tracking
export function trackEvent(
    eventName: string,
    properties?: Record<string, any>
) {
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.capture(eventName, properties)
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.identify(userId, properties)
}

export function setUserGroup(
    groupType: string,
    groupId: string,
    properties?: Record<string, any>
) {
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.group(groupType, groupId, properties)
}

export function trackOnboardingProgress(
    step: string,
    data?: Record<string, any>
) {
    trackEvent('onboarding_progress', {
        step,
        ...data,
    })
}

export function trackFeatureUsage(
    feature: string,
    action: string,
    properties?: Record<string, any>
) {
    trackEvent('feature_used', {
        feature,
        action,
        ...properties,
    })
}

// Vue plugin
export const PostHogPlugin = {
    install(app: App, options: PostHogConfig) {
        const instance = initPostHog(options)

        if (instance) {
            app.config.globalProperties.$posthog = instance
            app.provide('posthog', instance)
        }
    },
}

// Composable for using PostHog in components
export function usePostHog() {
    return {
        trackEvent,
        identifyUser,
        setUserGroup,
        trackOnboardingProgress,
        trackFeatureUsage,
        posthog: posthogInstance,
    }
}
